/**
 * VariationPanel — prompt textarea + "Generate" button + stacked sandboxed
 * iframes for each generated variation. One-click "Use this one" promotes
 * a variation into the editor's draft body.
 */

import { useState } from 'react'
import { Sparkles, CheckCircle2 } from 'lucide-react'
import { SandboxedPreview } from './SandboxedPreview'

export interface Variation {
  html: string
  meta?: { bytes?: number; userIntent?: string } | null
}

interface VariationPanelProps {
  currentHtml: string
  onGenerate: (args: { userIntent: string; count: number }) => Promise<{ variations: Variation[]; errors?: string[] }>
  onAccept: (html: string) => void
}

export function VariationPanel({ currentHtml, onGenerate, onAccept }: VariationPanelProps) {
  const [userIntent, setUserIntent] = useState('')
  const [count, setCount] = useState(2)
  const [busy, setBusy] = useState(false)
  const [variations, setVariations] = useState<Variation[]>([])
  const [errors, setErrors] = useState<string[]>([])

  async function handleGenerate() {
    if (!userIntent.trim()) return
    setBusy(true)
    setErrors([])
    try {
      const out = await onGenerate({ userIntent: userIntent.trim(), count })
      setVariations(out.variations || [])
      if (out.errors && out.errors.length) setErrors(out.errors)
    } catch (err) {
      setErrors([(err as Error).message])
      setVariations([])
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-3" data-testid="variation-panel">
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
        Describe the variation
      </label>
      <textarea
        value={userIntent}
        onChange={(e) => setUserIntent(e.target.value)}
        placeholder='e.g. "lean more halloween, pumpkins and purple" or "add a live countdown"'
        rows={3}
        className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800"
        aria-label="Variation prompt"
      />
      <div className="flex items-center gap-2">
        <label className="text-xs text-slate-500">
          Generate
          <select
            value={count}
            onChange={(e) => setCount(parseInt(e.target.value, 10) || 1)}
            className="mx-2 rounded border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-800"
            aria-label="Number of variations"
          >
            <option value={1}>1</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
          </select>
          variation{count === 1 ? '' : 's'}
        </label>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={!userIntent.trim() || busy || !currentHtml}
          className="flex items-center gap-1 rounded-md bg-butler-500 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-white hover:bg-butler-600 disabled:opacity-50"
        >
          <Sparkles className="h-3 w-3" />
          {busy ? 'Generating…' : 'Generate'}
        </button>
      </div>
      {errors.length > 0 && (
        <div
          role="alert"
          className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700"
        >
          <div className="font-semibold">Variation refused</div>
          <ul className="mt-1 list-disc pl-4">
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex flex-col gap-3">
        {variations.map((v, i) => (
          <div key={i} className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Variation {i + 1}
                {v.meta?.bytes ? ` · ${(v.meta.bytes / 1024).toFixed(1)} KB` : ''}
              </span>
              <button
                type="button"
                onClick={() => onAccept(v.html)}
                className="flex items-center gap-1 rounded-md bg-marquee-500 px-2.5 py-1 text-xs font-semibold uppercase tracking-wider text-white hover:bg-marquee-600"
                aria-label={`Use variation ${i + 1}`}
              >
                <CheckCircle2 className="h-3 w-3" />
                Use this one
              </button>
            </div>
            <SandboxedPreview html={v.html} height={300} />
          </div>
        ))}
      </div>
    </div>
  )
}
